/**
 * Account change — when a different person signs in, the page starts over.
 *
 * Everything workspace-scoped this page holds (the zustand stores, the
 * module-level tenant context, the permission and plan caches) was loaded for
 * the person who was signed in before. Signing in as someone else in the same
 * tab — directly, after a sign-out without a reload, or in another tab sharing
 * the session — used to keep all of it: on 2026-11-02 the Workflows settings
 * page showed workspace A's workflows and templates to the person in
 * workspace B, and `fetchWorkflows` seeds when it finds none, so stale context
 * could write rows.
 *
 * The sign-out reset clears only the user and tenant stores, and the
 * workspace refetch list misses stores (workflows, cycle templates, dozens of
 * screen-local caches). The one reset that provably drops all of it is a
 * reload, so a change of person reloads the page once. `AuthSessionSync`
 * decides and calls `reloadOnceForAccountChange`; this module is
 * dependency-free so the stores that seed rows can ask `isAccountChangeInProgress`
 * without an import cycle.
 */

const RELOADED_FOR_KEY = 'account-change-reload';
const LOOP_GUARD_MS = 60_000;

/** The auth user this page has loaded data for; null until the first one is known. */
let pageUserId: string | null = null;
/** Set from the moment a reload for a new person is decided until the page unloads. */
let changeInProgress = false;

export function getPageUserId(): string | null {
  return pageUserId;
}

/**
 * Record the person this page is loading data for. The first one wins until a
 * reload; `replace` is only for adopting a new person in place when a reload
 * was refused.
 */
export function notePageUser(userId: string, { replace = false }: { replace?: boolean } = {}): void {
  if (!pageUserId || replace) pageUserId = userId;
}

/**
 * True when the page holds data for one person and the session now names another.
 * Null on either side is not a change: a cold page has loaded nobody's data yet.
 */
export function isAccountChange(sessionUserId: string | null | undefined): boolean {
  return Boolean(pageUserId && sessionUserId && pageUserId !== sessionUserId);
}

/** True while the page is about to reload for a new person: nothing may be written for the old one. */
export function isAccountChangeInProgress(): boolean {
  return changeInProgress;
}

export interface AccountChangeReloadOptions {
  now?: number;
  reload?: () => void;
  /** Drops what survives a reload (persisted selections) so the new page starts cold. */
  beforeReload?: () => void;
}

/**
 * Reload once for `toUserId`. Returns false — and does nothing — when the page
 * already reloaded for this same person within the last minute, so a fault that
 * keeps the mismatch alive cannot loop; the caller then adopts the session in place.
 */
export function reloadOnceForAccountChange(
  toUserId: string,
  { now = Date.now(), reload = () => window.location.reload(), beforeReload }: AccountChangeReloadOptions = {},
): boolean {
  try {
    const raw = sessionStorage.getItem(RELOADED_FOR_KEY);
    if (raw) {
      const last = JSON.parse(raw) as { userId?: string; at?: number };
      if (last.userId === toUserId && typeof last.at === 'number' && now - last.at < LOOP_GUARD_MS) {
        return false;
      }
    }
  } catch {
    // Storage blocked or unreadable: reload once anyway.
  }
  try {
    sessionStorage.setItem(RELOADED_FOR_KEY, JSON.stringify({ userId: toUserId, at: now }));
  } catch {
    // Without storage we cannot guard a loop, but one reload is still right.
  }
  changeInProgress = true;
  try {
    beforeReload?.();
  } catch (err) {
    console.warn('[AccountChange] Could not clear the previous person’s saved selection:', err);
  }
  reload();
  return true;
}

/** Test hook: forget the page user and the in-progress flag. */
export function __resetAccountChangeForTests(): void {
  pageUserId = null;
  changeInProgress = false;
}
